/**
 * Shared UI components for search fields (events, products, memberships).
 * Used by SearchEvent, SearchProduct, SearchMembership and by SearchFormFields.
 */
import React from "react";
import Box from "@ui-kit/layout/Box/Box.web";
import Flex from "@ui-kit/layout/Flex/Flex.web";
import Text from "@ui-kit/typography/Text/Text.web";
import Skeleton from "@ui-kit/feedback/Skeleton/Skeleton.web";
import Divider from "@ui-kit/data-display/Divider/Divider.web";
import IconButton from "@ui-kit/form/IconButton/IconButton.web";
import Switch from "@ui-kit/form/Switch/Switch.web";
import { useTranslation } from "@shared/utils/internationalization";
import type { SearchFieldCounterConfig, SubItemType } from "./types";

export function SearchFieldCounter({
  items,
}: {
  items: Array<{ label: string; count: number }>;
}): JSX.Element {
  return (
    <Flex justify="space-between" mt="xs">
      {items.map((item) => (
        <Text key={item.label} fontSize="sm">
          <Text as="span" fontWeight="bold">
            {item.count}
          </Text>{" "}
          {item.label}
        </Text>
      ))}
    </Flex>
  );
}

export function SelectedItemCard<T extends SubItemType>({
  title,
  subtitle,
  badge,
  subItems,
  selectedSubItemIds,
  onDelete,
  onToggleSubItem,
  isLoading = false,
  testId,
}: {
  title: string;
  subtitle?: string;
  badge?: React.ReactNode;
  subItems?: T[];
  selectedSubItemIds: number[];
  onDelete: () => void;
  onToggleSubItem?: (subItemId: number, isSelected: boolean) => void;
  isLoading?: boolean;
  testId?: string;
}): JSX.Element {
  const { t } = useTranslation("common");
  return (
    <Box
      bg="bg.muted"
      borderRadius="md"
      p="md"
      width="100%"
      data-testid={testId}
    >
      {badge && <Box mb="2xs">{badge}</Box>}

      <Flex justifyContent="space-between" alignItems="flex-start">
        <Box>
          <Skeleton isLoaded={!isLoading}>
            <Text fontWeight="bold">{title}</Text>
          </Skeleton>
          {subtitle && (
            <Skeleton isLoaded={!isLoading} height="element.sm">
              <Text fontSize="sm" color="fg.muted">
                {subtitle}
              </Text>
            </Skeleton>
          )}
        </Box>
        <Skeleton isLoaded={!isLoading}>
          <IconButton.Ghost
            data-testid={testId ? `${testId}-delete` : undefined}
            aria-label={t("Delete item")}
            onClick={onDelete}
            iconSize="20px"
            iconName="Trash"
            iconColor="fg.muted"
          />
        </Skeleton>
      </Flex>

      {subItems && subItems.length > 0 && onToggleSubItem && (
        <Box mt="md">
          {subItems.map((subItem, index) => (
            <Box key={subItem.id}>
              {index !== 0 && <Divider my="2xs" />}
              <Flex justifyContent="space-between" alignItems="center">
                <Skeleton isLoaded={!isLoading}>
                  <Text fontSize="sm">{subItem.name}</Text>
                </Skeleton>
                <Skeleton isLoaded={!isLoading}>
                  <Switch
                    data-testid={testId ? `${testId}-toggle-${subItem.id}` : ""}
                    id={`subitem-${subItem.id}`}
                    isChecked={selectedSubItemIds.includes(subItem.id)}
                    onChange={() =>
                      onToggleSubItem(
                        subItem.id,
                        !selectedSubItemIds.includes(subItem.id)
                      )
                    }
                  />
                </Skeleton>
              </Flex>
            </Box>
          ))}
        </Box>
      )}
    </Box>
  );
}

export function SelectedItemTags<T extends { id: number; name: string }>({
  items,
  onRemove,
}: {
  items: T[];
  onRemove: (id: number) => void;
}): JSX.Element {
  return (
    <Flex gap="xs" flexWrap="wrap">
      {items.map((item) => (
        <Flex
          key={item.id}
          alignItems="center"
          gap="2xs"
          bg="bg.muted"
          borderRadius="sm"
          px="xs"
          py="2xs"
        >
          <Text fontSize="sm">{item.name}</Text>
          <IconButton.Ghost
            aria-label={`Remove ${item.name}`}
            onClick={() => onRemove(item.id)}
            iconSize="14px"
            iconName="X"
            iconColor="fg.muted"
            size="2xs"
          />
        </Flex>
      ))}
    </Flex>
  );
}

export function SelectedItemList<T extends { id: number; name: string }>({
  items,
  onRemove,
}: {
  items: T[];
  onRemove: (id: number) => void;
}): JSX.Element {
  return (
    <Flex direction="column" gap="xs">
      {items.map((item) => (
        <Flex
          key={item.id}
          justifyContent="space-between"
          alignItems="center"
          p="xs"
          bg="bg.subtle"
          borderRadius="sm"
        >
          <Text fontSize="sm">{item.name}</Text>
          <IconButton.Ghost
            aria-label={`Remove ${item.name}`}
            onClick={() => onRemove(item.id)}
            iconSize="16px"
            iconName="X"
            iconColor="fg.muted"
            size="xs"
          />
        </Flex>
      ))}
    </Flex>
  );
}

export function calculateCounters<T>(
  counters: SearchFieldCounterConfig[] | undefined,
  items: T[],
  subItemSelections?: Map<number, number[]>
): Array<{ label: string; count: number }> {
  if (!counters || items.length === 0) return [];

  return counters.map((counter) => {
    const count = counter.getValue(items, subItemSelections);
    const label =
      typeof counter.label === "function"
        ? counter.label(count)
        : counter.label;
    return { label, count };
  });
}
